import * as React from "react";
import { Loader2 } from "lucide-react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

/**
 * Material Design v3 Button Component
 * Following MD v3 guidelines for buttons with state layers and proper elevation
 */

/**
 * Button variants following Material Design v3 guidelines
 *
 * @typedef {string} ButtonVariant
 *
 * - "default" - Primary filled button with high emphasis. Use for primary actions and CTAs.
 *   Uses primary color with on-primary text color.
 *
 * - "destructive" - Error/warning button for destructive actions like delete or remove.
 *   Uses error color with on-error text color.
 *
 * - "outline" - Medium emphasis button with outline. Use for secondary actions.
 *   Uses transparent background with border and text in primary color.
 *
 * - "secondary" - Alternative styling using secondary color. Use for complementary actions.
 *   Uses secondary color with on-secondary text color.
 *
 * - "ghost" - Low emphasis button with no background or border. Use for tertiary actions.
 *   Shows background color only on hover.
 *
 * - "link" - Appears as a text link with no background. Use for navigation or minor actions.
 *   Uses primary text color with underline on hover.
 *
 * - "tertiary" - Uses MD v3 tertiary color system. Good for informational or help actions.
 *   Uses tertiary color with on-tertiary text color.
 *
 * - "primary-container" - MD v3 container variant for softer primary emphasis.
 *   Uses a lighter/darker container color derived from primary.
 *   Ideal for grouped actions that relate to primary actions but need less emphasis.
 *   Creates visual harmony while maintaining distinction.
 *
 * - "secondary-container" - MD v3 container variant for softer secondary emphasis.
 *   Uses a lighter/darker container color derived from secondary.
 *   Perfect for alternative options within a group of related actions.
 *   Maintains color harmony while creating visual hierarchy.
 */
/**
 * Button variants and sizes are now defined in the buttonVariants CVA below
 */
const buttonVariants = cva(
  // Base styles applied to all variants
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded text-sm font-medium transition-colors outline-none focus-visible:ring-1 focus-visible:ring-primary/30 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        // High emphasis button for primary actions
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        
        // Error/warning button for destructive actions
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90 focus-visible:ring-destructive/30",
        
        // Medium emphasis button with outline
        outline: "border border-input-border bg-background hover:bg-accent/20 hover:text-accent-foreground",
        
        // Alternative styling using secondary color
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        
        // Low emphasis button with no background
        ghost: "hover:bg-md-surface-variant hover:text-accent-foreground",
        
        // Text link style button
        link: "text-primary underline-offset-4 hover:underline",
        
        // Uses MD v3 tertiary color system
        tertiary: "bg-md-tertiary text-md-on-tertiary hover:bg-md-tertiary/90",
        
        // Primary container variant - uses a softer version of the primary color
        "primary-container": "bg-md-primary-container text-md-on-primary-container hover:bg-md-primary-container/90",
        
        // Secondary container variant - uses a softer version of the secondary color
        "secondary-container": "bg-md-secondary-container text-md-on-secondary-container hover:bg-md-secondary-container/90",
      },
      size: {
        default: "h-10 px-4 py-2 has-[>svg]:px-3",
        sm: "h-9 rounded-sm gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-11 rounded-lg px-6 has-[>svg]:px-4",
        icon: "size-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

/**
 * Button props interface with proper documentation
 */
export interface ButtonProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement>,
        VariantProps<typeof buttonVariants> {
    /**
     * When true, the button will render its children as the root element using Radix UI's Slot.
     * This allows you to compose the button's functionality and styles with other components.
     */
    asChild?: boolean;
    /**
     * When true, displays a loading spinner and disables the button
     */
    loading?: boolean;
    /**
     * When true, the button will take up the full width of its container
     */
    fullWidth?: boolean;
}

/**
 * Button component following Material Design v3 guidelines
 */
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    (
        {
            className,
            variant,
            size,
            asChild = false,
            loading = false,
            fullWidth = false,
            children,
            disabled,
            ...props
        },
        ref
    ) => {
        // Combine disabled state with loading state
        const isDisabled = disabled || loading;
        
        // Use Slot if asChild is true, otherwise use button
        const Comp = asChild ? Slot : "button";
        
        // Combine all styles using CVA
        const buttonStyles = cn(
            buttonVariants({ variant, size }),
            fullWidth ? "w-full" : "",
            className
        );
        
        return (
            <Comp
                className={buttonStyles}
                ref={ref}
                disabled={isDisabled}
                {...props}
            >
                {loading && <Loader2 className="size-4 animate-spin mr-2" />}
                {children}
            </Comp>
        );
    }
);

Button.displayName = "Button";

export { Button };
